import type { SelectOption } from '@wundo/ui';
import type { MeetingTypeOption } from '@/lib/withCurrentTemplate.ts';
import { m } from '@/paraglide/messages.js';

/**
 * Whether writing a meeting down is a choice of gesprekstype at all.
 *
 * With one gesprekstype there is nothing to choose: the form applies it and
 * says nothing about it, so an organisation with a single type keeps a form of
 * a title, a day and an hour. From two onwards nothing may be guessed — a
 * meeting only the user knows about has no guests, no history and no kind to
 * infer one from — so the dropdown appears and has to be answered.
 */
export function meetingTypeIsAChoice(templates: MeetingTypeOption[]): boolean {
  return templates.length > 1;
}

/**
 * What a form starts on: the only gesprekstype there is, or nothing — which
 * leaves the save button disabled until the user picks one.
 */
export function defaultMeetingTemplateId(templates: MeetingTypeOption[]): string {
  return templates.length === 1 ? templates[0].id : '';
}

/**
 * The gesprekstypes as a dropdown reads them.
 *
 * While nothing is chosen the list opens on a line that is not a type, so a
 * native select cannot answer the question by simply being drawn — the user has
 * to move it. Once they have, that line is gone: unpicking a gesprekstype is
 * not something the form allows.
 */
export function meetingTypeOptions(
  templates: MeetingTypeOption[],
  chosen: string,
): SelectOption[] {
  const options = templates.map((template) => ({
    value: template.id,
    label: template.name,
  }));

  return chosen === ''
    ? [{ value: '', label: m.manual_meeting_type_placeholder() }, ...options]
    : options;
}
